import { useId } from 'react';
import { delay, Glow, iconClassName, styles, svgProps } from '@/components/service-showcase/icons/shared';
import type { IServiceShowcaseSvgIconProps } from '@/components/service-showcase/icons/type';

// Twinkling stars inside the orbit (r=56), outside the planet (r=9)
const STARS = [
  { cx: 168, cy: 76, r: 1.0, d: 0 },
  { cx: 215, cy: 85, r: 0.8, d: 0.8 },
  { cx: 158, cy: 132, r: 1.1, d: 1.5 },
  { cx: 212, cy: 145, r: 0.9, d: 2.1 },
  { cx: 234, cy: 124, r: 0.8, d: 0.4 },
];

const ORBIT_NODES = [
  { cx: 190, cy: 54, d: 0.4 },
  { cx: 246, cy: 110, d: 0.52 },
  { cx: 190, cy: 166, d: 0.64 },
  { cx: 134, cy: 110, d: 0.76 },
];

// Flash blips synced to orbit sweep. Period=6s, starts 2.2s, 2%=0.12s → delay=t_hit−0.12
const NODE_BLIPS = [
  { cx: 246, cy: 110, d: 2.08 },
  { cx: 190, cy: 166, d: 3.58 },
  { cx: 134, cy: 110, d: 5.08 },
  { cx: 190, cy: 54, d: 6.58 },
];

// Planet wave layers – all clipped to the planet circle (r=9, center 190,110)
// Stacked from large-to-small so top of circle gets the most layers → appears brightest.
// Each path starts at y=101 (top of circle) and fills down to a concave wavy bottom edge.
//   Layer 3 (tallest, subtle):   bottom wave ≈ y 114.5 on sides, y 112 at centre
//   Layer 2 (medium):            bottom wave ≈ y 110   on sides, y 107.5 at centre
//   Layer 1 (top cap, brightest): bottom wave ≈ y 106   on sides, y 103.5 at centre
const WAVE_LAYERS = [
  { d: 'M 181 101 L 199 101 L 199 114.5 Q 190 112 181 115 Z', o: 0.18 },
  { d: 'M 181 101 L 199 101 L 199 110 Q 190 107.5 181 110.5 Z', o: 0.2 },
  { d: 'M 181 101 L 199 101 L 199 106 Q 190 103.5 181 106.5 Z', o: 0.28 },
];

export default function OrbitLoopIcon(props: IServiceShowcaseSvgIconProps) {
  const { active = true, className, ...rest } = props;
  const id = useId();
  const clipId = `${id}c`;

  return (
    <svg {...svgProps} {...rest} className={iconClassName(active, className)}>
      <defs>
        <clipPath id={clipId}>
          <circle cx='190' cy='110' r='9' />
        </clipPath>
      </defs>

      <Glow cx={190} cy={110} r={22} opacity={0.28} />

      {/* Stars inside orbit */}
      {STARS.map((s) => (
        <circle
          key={`${s.cx}-${s.cy}`}
          cx={s.cx} cy={s.cy} r={s.r}
          fill='currentColor'
          className={styles.star}
          style={delay(`${s.d}s`)}
        />
      ))}

      {/* Static orbit track */}
      <circle cx='190' cy='110' r='56' stroke='currentColor' strokeOpacity='0.12' strokeWidth='1.4' />

      {/* Orbiting arc + arrow */}
      <g className={styles.orbit} style={{ transformOrigin: '190px 110px' }}>
        <circle
          cx='190' cy='110' r='56'
          stroke='currentColor' strokeWidth='1.4' strokeOpacity='0.55'
          strokeDasharray='245 110'
          className={styles.ring} style={delay('0.15s')}
        />
        <polygon points='246,104 246,116 256,110' fill='currentColor' fillOpacity='0.8' />
      </g>

      {/* Planet – base sphere + layered wave bands clipped inside */}
      <g className={styles.core}>
        {/* Ocean base */}
        <circle
          cx='190' cy='110' r='9'
          fill='currentColor' fillOpacity='0.18'
          stroke='currentColor' strokeOpacity='0.6' strokeWidth='1'
        />
        {/* Atmosphere layers — each fills from top down to a concave wave edge */}
        <g clipPath={`url(#${clipId})`}>
          {WAVE_LAYERS.map((l, i) => (
            <path key={i} d={l.d} fill='currentColor' fillOpacity={l.o} />
          ))}
        </g>
      </g>

      {/* Orbit waypoints */}
      {ORBIT_NODES.map(({ cx, cy, d }) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx} cy={cy} r='4'
          fill='currentColor' fillOpacity='0.9'
          className={styles.pop} style={delay(`${d}s`)}
        />
      ))}

      {/* Node flash blips */}
      {NODE_BLIPS.map(({ cx, cy, d }) => (
        <circle
          key={`blip-${cx}-${cy}`}
          cx={cx} cy={cy} r='6'
          fill='currentColor' fillOpacity='0.65'
          className={styles.orbitBlip} style={delay(`${d}s`)}
        />
      ))}
    </svg>
  );
}
